/*
  Списъкът с желания.

  Кой е човекът НЕ се подава оттук: App Proxy добавя logged_in_customer_id към
  подписаната заявка и ERP вярва само на него. Затова:

  · влязъл клиент — списъкът е в ERP;
  · анонимен — ERP връща logged_in:false и празно, а неговият списък стои в
    браузъра му. При влизане се влива (merge) и се чисти оттук.

  ERP пази две неща сам и те не се повтарят тук: повторното слагане не дублира,
  а плоча, която човекът е махнал, не се връща при вливане. Затова целият местен
  списък се праща спокойно.

  Всеки отговор носи ЦЕЛИЯ списък, затова сърцата се рисуват от един източник —
  множеството `handles`, — а не всяко за себе си.

  Ако ERP върне `items` (адрес + дата на добавяне), редът показва и кога е
  запазена плочата. Без тях редът е същият, само без този надпис — датата не се
  измисля.
*/
(() => {
  const KEY = 'gm-wishlist';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const endpoint = root + 'apps/gramofonika/wishlist';

  let handles = new Set();
  let added = new Map(); // адрес → дата на добавяне, ако ERP я дава
  let loggedIn = false;
  let ready = false;
  /* Избраните отметки по групи и избраната подредба. Държат се тук, а не в
     адреса: списъкът е на човека, страницата не се презарежда и няма какво да
     се сподели с линк. */
  let picked = { state: new Set(), kind: new Set(), format: new Set() };
  let sortBy = 'added';
  let foldApplied = false;

  const local = {
    read() {
      try {
        const raw = JSON.parse(localStorage.getItem(KEY));
        return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
      } catch (error) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (error) {
        /* частен режим или пълно хранилище — сърцето пак работи до презареждане */
      }
    },
    clear() {
      try {
        localStorage.removeItem(KEY);
      } catch (error) {
        /* същото */
      }
    },
  };

  /* ── Номерът на браузъра ─────────────────────────────────────────────
     ERP брои и желанията на невлезли посетители (23.10). За да ги различава
     един от друг, темата дава случаен номер, който стои в този браузър.

     СЪГЛАСИЕТО РЕШАВА. Номерът се създава и праща само когато посетителят е
     разрешил статистика (`customerPrivacy.analyticsProcessingAllowed`).
     Оттегли ли съгласието, ключът се трие и повече нищо не тръгва — затова
     проверката е при ВСЯКО ползване, а не веднъж при зареждане.

     Липсва ли съгласие или хранилище, всичко работи както преди: списъкът си
     остава в браузъра, а ERP просто не брои този човек. */
  const VISITOR_KEY = 'gm_visitor';
  const VISITOR_SHAPE = /^[A-Za-z0-9_-]{16,64}$/;
  let visitorIsNew = false;

  function consentGiven() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(
        privacy &&
        typeof privacy.analyticsProcessingAllowed === 'function' &&
        privacy.analyticsProcessingAllowed()
      );
    } catch (error) {
      return false;
    }
  }

  function makeVisitor() {
    try {
      if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
      const bytes = new Uint8Array(24);
      crypto.getRandomValues(bytes);
      return [...bytes].map((b) => b.toString(36).padStart(2, '0')).join('').slice(0, 48);
    } catch (error) {
      return '';
    }
  }

  function forgetVisitor() {
    try {
      localStorage.removeItem(VISITOR_KEY);
    } catch (error) {
      /* нищо за триене */
    }
  }

  function visitor() {
    if (!consentGiven()) {
      forgetVisitor();
      return '';
    }
    try {
      let id = localStorage.getItem(VISITOR_KEY);
      if (!id || !VISITOR_SHAPE.test(id)) {
        id = makeVisitor();
        if (!id) return '';
        localStorage.setItem(VISITOR_KEY, id);
        visitorIsNew = true;
      }
      return id;
    } catch (error) {
      return '';
    }
  }

  /* Номерът се долепя само там, където ERP го чака: при слагане, махане и
     вливане. `list` и `resolve` не питат за човек. */
  function withVisitor(body) {
    const id = visitor();
    return id ? { ...body, visitor: id } : body;
  }

  async function post(body) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  }

  /*
    ERP може да върне или прост списък с адреси, или списък с обекти
    {handle, added_at}. И двете дават едно и също множество; датите просто
    липсват при първия вид.
  */
  function absorb(payload) {
    const next = new Set();
    const dates = new Map();

    const items = payload && Array.isArray(payload.items) ? payload.items : null;
    if (items) {
      for (const item of items) {
        if (!item || typeof item.handle !== 'string') continue;
        next.add(item.handle);
        if (item.added_at) dates.set(item.handle, item.added_at);
      }
    } else {
      for (const handle of (payload && payload.handles) || []) {
        if (typeof handle === 'string') next.add(handle);
      }
    }

    handles = next;
    added = dates;
  }

  function paintOne(button) {
    const saved = handles.has(button.dataset.gmHandle);
    button.dataset.gmSaved = saved ? 'true' : 'false';
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');

    const label = saved ? button.dataset.gmLabelRemove : button.dataset.gmLabelAdd;
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);

    const text = button.querySelector('[data-gm-wish-label]');
    if (text) text.textContent = saved ? button.dataset.gmLabelSaved : button.dataset.gmLabelAdd;
  }

  /* Броячът до сърцето в хедъра и в чекмеджето на профила. Празен списък не
     показва нула: «0» до кошницата изглежда като повреда, не като празнота. */
  function paintCount() {
    const n = handles.size;
    for (const node of document.querySelectorAll('[data-gm-wish-count]')) {
      node.textContent = n ? String(n) : '';
      node.hidden = n === 0;
    }
  }

  function paint() {
    document.querySelectorAll('[data-gm-wish]').forEach(paintOne);
    paintCount();
    document.dispatchEvent(
      new CustomEvent('gm:wishlist', { detail: { handles: [...handles], loggedIn, ready } })
    );
  }

  /* ── Лентата при първото запазено сърце ──────────────────────────────
     Сърцето запълва и нищо повече не се случва; човек, натиснал го за пръв
     път, не знае, че има списък. Лентата казва какво стана и води натам.

     Само ВЕДНЪЖ на посещение и само при ЗАПАЗВАНЕ, не при махане. Бележката
     стои в `sessionStorage`: следващия път напомнянето пак е уместно. Частен
     режим или пълно хранилище не бива да гърми — тогава лентата просто се
     показва, което е по-доброто от двете. */
  const TOAST_KEY = 'gm-wishlist-toast';
  let toastTimer = 0;

  function showToast() {
    const toast = document.querySelector('[data-gm-wish-toast]');
    if (!toast) return;

    try {
      if (sessionStorage.getItem(TOAST_KEY) === '1') return;
      sessionStorage.setItem(TOAST_KEY, '1');
    } catch (error) {
      /* без хранилище лентата се показва веднъж на зареждане */
      if (toast.dataset.gmShown === 'true') return;
    }
    toast.dataset.gmShown = 'true';

    toast.hidden = false;
    // Два кадъра: първият показва елемента, вторият пуска прехода.
    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-gm-in')));

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  function hideToast() {
    const toast = document.querySelector('[data-gm-wish-toast]');
    if (!toast) return;
    toast.classList.remove('is-gm-in');
    setTimeout(() => {
      if (!toast.classList.contains('is-gm-in')) toast.hidden = true;
    }, 300);
  }

  async function toggle(button) {
    if (button.dataset.gmBusy === 'true') return;

    const handle = button.dataset.gmHandle;
    const wanted = !handles.has(handle);

    // Сърцето се запълва веднага, преди отговора: изчакване при натискане на
    // сърце се чете като «не се получи».
    if (wanted) handles.add(handle);
    else handles.delete(handle);
    paint();
    if (wanted) showToast();

    if (!loggedIn) {
      local.write([...handles]);
      dropRow(button, handle);
      /* Списъкът на анонимния си остава в браузъра — ERP само брои. Затова
         заявката се пуска и не се чака: падне ли, сърцето не се връща назад,
         защото истината е местният списък, не отговорът. */
      const id = visitor();
      if (id) {
        post(
          withVisitor({
            action: wanted ? 'add' : 'remove',
            handle,
            product_id: button.dataset.gmProductId,
          })
        ).catch(() => {});
      }
      return;
    }

    button.dataset.gmBusy = 'true';
    try {
      const { ok, data } = await post(
        withVisitor({
          action: wanted ? 'add' : 'remove',
          handle,
          product_id: button.dataset.gmProductId,
        })
      );

      if (ok && data.success) {
        absorb(data);
      } else {
        // Връщаме сърцето, иначе човек мисли, че е запазено, а не е.
        if (wanted) handles.delete(handle);
        else handles.add(handle);
      }
    } catch (error) {
      if (wanted) handles.delete(handle);
      else handles.add(handle);
    } finally {
      button.dataset.gmBusy = 'false';
      paint();
      dropRow(button, handle);
    }
  }

  /* Махнатата плоча няма какво да прави на страницата «Желани». */
  function dropRow(button, handle) {
    const page = document.querySelector('[data-gm-wishlist-page]');
    if (!page || handles.has(handle)) return;

    const row = button.closest('.gm-wishrow, .gm-sleeve');
    if (row) row.remove();
    refreshPage();
  }

  /* Сърцето вече живее ВЪТРЕ в блока с бутоните, а Horizon пререндерира този
     блок при смяна на състоянието. Сървърът го връща празно — състоянието не се
     рисува от Liquid, — затова всяко ново сърце се изчертава в мига, в който
     влезе в страницата. Без това запазена плоча си «губеше» сърцето при избор
     на друго състояние. */
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches('[data-gm-wish]')) paintOne(node);
        node.querySelectorAll('[data-gm-wish]').forEach(paintOne);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gm-wish]');
    if (!button) return;
    // Сърцето върху обложката стои над връзката на картата.
    event.preventDefault();
    event.stopPropagation();
    toggle(button);
  });

  /* ── Кошница ───────────────────────────────────────────────────────────
     Добавянето минава през /cart/add.js, а после Horizon се уведомява със
     собственото си събитие, за да се обновят броячът и панелът. Класът
     CartLinesUpdateEvent е в модул и не се стига оттук, но слушателите четат
     само `action` и `promise`, затова се подава обикновено събитие с тези две
     полета — същите, каквито дава Horizon. */
  function cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);
  }

  async function addToCart(items) {
    const sections = cartSectionIds();
    const body = { items };
    if (sections.length) body.sections = sections.join(',');

    const response = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'cart');

    // Броячът иска ОБЩИЯ брой в кошницата, а /cart/add.js връща само добавеното.
    let count = 0;
    try {
      const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      const json = await cart.json();
      count = json.item_count || 0;
    } catch (error) {
      /* броячът остава какъвто е — по-добре, отколкото грешно число */
    }

    const event = new Event('shopify:cart:lines-update', { bubbles: true });
    event.action = 'add';
    event.context = 'wishlist';
    event.promise = Promise.resolve({
      cart: { totalQuantity: count },
      detail: { itemCount: count, sections: data.sections },
    });
    document.dispatchEvent(event);

    return data;
  }

  /* ── Страницата «Списък с желани» ─────────────────────────────────────
     Редовете се искат от самите продукти (`/products/<адрес>?section_id=…`),
     за да се смятат цената, състоянието и наличността с данните на магазина,
     а не втори път тук. */
  function pageParts() {
    const page = document.querySelector('[data-gm-wishlist-page]');
    if (!page) return null;
    return {
      page,
      rows: page.querySelector('[data-gm-wishlist-grid]'),
      empty: page.querySelector('[data-gm-wishlist-empty]'),
      tabs: page.querySelector('[data-gm-wishlist-tabs]'),
      rule: page.querySelector('[data-gm-wishlist-rule]'),
      body: page.querySelector('[data-gm-wishlist-body]'),
      facets: page.querySelector('[data-gm-wish-facets]'),
      chips: page.querySelector('[data-gm-wish-chips]'),
      activeCount: page.querySelectorAll('[data-gm-wish-active], [data-gm-wish-active-mobile]'),
      foot: page.querySelector('[data-gm-wishlist-foot]'),
      summary: page.querySelector('[data-gm-wishlist-summary]'),
      availableCount: page.querySelector('[data-gm-wishlist-available]'),
      addAll: page.querySelector('[data-gm-wishlist-add-all]'),
      bulkError: page.querySelector('[data-gm-wishlist-bulk-error]'),
      none: page.querySelector('[data-gm-wishlist-none]'),
    };
  }

  /* Надписите идват от Liquid през data-атрибути на самата страница. За едно и
     за много има отделен надпис — коя форма да е, се знае чак тук, при числото. */
  function plural(page, base, n) {
    const set = page.dataset;
    const text = n === 1 ? set[base + 'One'] : set[base + 'Other'];
    return text ? text.replace('{n}', n) : String(n);
  }

  /* ── Панелът отляво ──────────────────────────────────────────────────
     Филтрите НЕ идват от Shopify: списъкът е на човека, а не колекция в
     магазина, значи няма `collection.filters` и няма адрес за презареждане.
     Затова групите се пълнят от самите карти — всяка носи състояние, вид,
     формат и цена, — а пресяването и подреждането стават тук, върху вече
     изчертаното.

     Стойност с нула плочи не се рисува изобщо: в колекция празната отметка
     казва «няма такива», тук би била лъжа — списъкът е толкова, колкото е. */
  const ГРУПИ = {
    state: (row) => (row.dataset.gmWishState === 'available' ? 'available' : 'waiting'),
    kind: (row) => row.dataset.gmKind || '',
    format: (row) => row.dataset.gmFormat || '',
  };

  function стойностНадпис(група, стойност, row) {
    if (група === 'state' || група === 'kind') {
      // Надписът вече е изчертан от Liquid вътре в картата — взима се оттам,
      // за да не се превежда втори път в скрипта.
      const значка = row.querySelector('.gm-wishcard__status');
      if (група === 'state' && стойност === 'waiting' && значка) return значка.textContent.trim();
    }
    return стойност;
  }

  function buildFacets(p, rows) {
    if (!p.facets) return;

    for (const група of Object.keys(ГРУПИ)) {
      const списък = p.facets.querySelector('[data-gm-wish-list="' + група + '"]');
      const кутия = p.facets.querySelector('[data-gm-wish-group="' + група + '"]');
      if (!списък || !кутия) continue;

      const броеве = new Map();
      for (const row of rows) {
        const стойност = ГРУПИ[група](row);
        if (!стойност) continue;
        броеве.set(стойност, (броеве.get(стойност) || 0) + 1);
      }

      // Една-единствена стойност не е филтър: нищо не пресява.
      if (броеве.size < 2) {
        кутия.hidden = true;
        списък.innerHTML = '';
        picked[група].clear();
        continue;
      }
      кутия.hidden = false;

      const редове = [...броеве.entries()].sort((a, b) => b[1] - a[1]);
      списък.innerHTML = редове
        .map(([стойност, брой]) => {
          const избрана = picked[група].has(стойност);
          const надпис = надписНа(p, група, стойност);
          return (
            '<li><label class="gm-facets__option' + (избрана ? ' is-on' : '') + '">' +
            '<input type="checkbox" data-gm-wish-facet="' + група + '" value="' +
            String(стойност).replace(/"/g, '&quot;') + '"' + (избрана ? ' checked' : '') + '>' +
            '<span class="gm-facets__box" aria-hidden="true"></span>' +
            '<span class="gm-facets__label">' + надпис + '</span>' +
            '<span class="gm-facets__count">' + брой + '</span>' +
            '</label></li>'
          );
        })
        .join('');
    }

    const избрани = Object.values(picked).reduce((n, набор) => n + набор.size, 0);
    for (const знак of p.activeCount || []) {
      знак.textContent = String(избрани);
      знак.hidden = избрани === 0;
    }
    buildChips(p);
  }

  /* Редът с приложените филтри — същият вид като в колекциите
     (`gramofonika-active-filters`). Там всяко чипче е връзка към адрес без
     този филтър; тук е бутон, защото няма адрес. «Изчисти» се появява чак при
     два приложени филтъра, както е и там: при един чипчето до него вече го
     маха и два бутона за едно действие се четат като два различни избора. */
  function buildChips(p) {
    if (!p.chips) return;
    const редове = [];
    for (const [група, набор] of Object.entries(picked)) {
      for (const стойност of набор) {
        редове.push(
          '<button type="button" class="gm-active-filters__chip" data-gm-wish-drop="' +
          група + '" value="' + String(стойност).replace(/"/g, '&quot;') + '">' +
          '<span>' + надписНа(p, група, стойност) + '</span>' +
          '<span aria-hidden="true">×</span></button>'
        );
      }
    }
    if (редове.length > 1) {
      редове.push(
        '<button type="button" class="gm-active-filters__clear" data-gm-wish-clear>' +
        (p.page.dataset.gmWordClear || '') + '</button>'
      );
    }
    p.chips.innerHTML = редове.join('');
    p.chips.hidden = редове.length === 0;
  }

  /* Надписът на стойност: състоянията и видовете идват преведени от Liquid,
     форматите са както са записани на плочата. */
  function надписНа(p, група, стойност) {
    const речник = {
      available: p.page.dataset.gmWordStateAvailable,
      waiting: p.page.dataset.gmWordStateWaiting,
      new: p.page.dataset.gmWordKindNew,
      used: p.page.dataset.gmWordKindUsed,
    };
    return речник[стойност] || стойност;
  }

  /* Картата минава, ако във ВСЯКА група или няма избрано, или тя е сред
     избраните. Между групите «и», вътре в групата «или» — както е навсякъде. */
  function matches(row) {
    for (const [група, вземи] of Object.entries(ГРУПИ)) {
      const набор = picked[група];
      if (набор.size && !набор.has(вземи(row))) return false;
    }
    return true;
  }

  function sortRows(p, rows) {
    const подредби = {
      added: null, // редът, в който е дошъл списъкът — най-скоро добавените отгоре
      name: (a, b) => (a.dataset.gmName || '').localeCompare(b.dataset.gmName || '', 'bg'),
      'price-up': (a, b) => Number(a.dataset.gmPrice || 0) - Number(b.dataset.gmPrice || 0),
      'price-down': (a, b) => Number(b.dataset.gmPrice || 0) - Number(a.dataset.gmPrice || 0),
    };
    const правило = подредби[sortBy];
    if (!правило) return;

    const подредени = [...rows].sort(правило);
    for (const row of подредени) {
      // Картата идва увита в `div.shopify-section`; мести се обвивката, инак
      // редът на страницата не се мени.
      const носител = row.closest('.shopify-section') || row;
      p.rows.appendChild(носител);
    }
  }

  /* Обобщението, броячите и разделите — всичко се чете от самите редове, за да
     не се смята състоянието на плочата на две места. */
  function refreshPage() {
    const p = pageParts();
    if (!p) return;

    const rows = [...p.rows.querySelectorAll('[data-gm-wish-row]')];
    const available = rows.filter((row) => row.dataset.gmWishState === 'available');
    const waiting = rows.filter((row) => row.dataset.gmWishState !== 'available');

    if (!rows.length) {
      p.rows.hidden = true;
      p.tabs.hidden = true;
      if (p.rule) p.rule.hidden = true;
      p.foot.hidden = true;
      if (p.body) p.body.hidden = true;
      p.summary.textContent = ' ';
      p.empty.hidden = !ready;
      return;
    }

    p.empty.hidden = true;
    p.rows.hidden = false;
    p.tabs.hidden = false;
    if (p.rule) p.rule.hidden = false;
    p.foot.hidden = false;
    if (p.body) p.body.hidden = false;

    p.summary.textContent = [
      plural(p.page, 'gmWordRecords', rows.length),
      plural(p.page, 'gmWordAvailable', available.length),
      plural(p.page, 'gmWordWaiting', waiting.length),
    ].join(' · ');

    if (p.availableCount) {
      p.availableCount.textContent = plural(p.page, 'gmWordRecords', available.length);
    }
    if (p.addAll) p.addAll.disabled = available.length === 0;

    buildFacets(p, rows);
    sortRows(p, rows);
    if (!foldApplied) {
      foldApplied = true;
      let сгънато = false;
      try {
        сгънато = localStorage.getItem(FOLD_KEY) === '1';
      } catch (error) {
        сгънато = false;
      }
      applyFold(сгънато);
    }

    let shownCount = 0;
    for (const row of rows) {
      const shown = matches(row);
      row.hidden = !shown;
      if (shown) shownCount += 1;
    }

    // Разделът може да е празен, без списъкът да е — тогава се казва точно това,
    // вместо да остане дупка между заглавието и подвала.
    if (p.none) {
      p.none.textContent = p.page.dataset.gmWordNone || '';
      p.none.hidden = shownCount > 0;
    }
  }

  function stampDates() {
    if (!added.size) return;
    const page = document.querySelector('[data-gm-wishlist-page]');
    if (!page) return;

    const pattern = page.dataset.gmWordAdded || '{d}';
    let format;
    try {
      format = new Intl.DateTimeFormat(document.documentElement.lang || 'bg', {
        day: 'numeric',
        month: 'long',
      });
    } catch (error) {
      format = null;
    }

    for (const row of page.querySelectorAll('[data-gm-wish-row]')) {
      const stamp = added.get(row.dataset.gmHandle);
      const slot = row.querySelector('[data-gm-wish-added]');
      if (!stamp || !slot) continue;

      const date = new Date(stamp);
      const text = format && !isNaN(date) ? format.format(date) : String(stamp);
      slot.textContent = pattern.replace('{d}', text);
    }
  }

  async function renderPage() {
    const p = pageParts();
    if (!p) return;

    const list = [...handles];

    if (!list.length) {
      p.rows.innerHTML = '';
      refreshPage();
      return;
    }

    const section = p.page.dataset.gmCardSection;
    const parts = await Promise.all(
      list.map((handle) =>
        fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=' + section)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );

    // Плоча, която вече я няма в магазина, връща празно и просто не се рисува —
    // по-добре с един ред по-малко, отколкото с дупка или с грешка. Стар адрес
    // пък може да се пренасочи, а при пренасочване `section_id` не винаги
    // пътува — тогава идва цяла страница и тя не влиза в решетката.
    const isRow = (html) => html && !/^\s*<(!doctype|html)/i.test(html);
    p.rows.innerHTML = parts.filter(isRow).join('');

    stampDates();
    refreshPage();
    document.querySelectorAll('[data-gm-wish]').forEach(paintOne);
  }

  /* Отметка в панела, бутонът «Изчисти» и подредбата. Нищо не се иска наново
     от магазина: картите вече са тук, менят се само видимостта и редът. */
  document.addEventListener('change', (event) => {
    const поле = event.target.closest('[data-gm-wish-facet]');
    if (поле) {
      const група = поле.dataset.gmWishFacet;
      if (!picked[група]) return;
      if (поле.checked) picked[група].add(поле.value);
      else picked[група].delete(поле.value);
      refreshPage();
      return;
    }

  });

  /* Сгъването на панела: класът, надписът и паметта са същите като в
     колекциите, затова и ключът в localStorage е техният — човек, който е
     скрил филтрите в каталога, ги намира скрити и тук. */
  const FOLD_KEY = 'gm:facets:folded';

  function applyFold(folded) {
    const тяло = document.querySelector('[data-gm-wishlist-body]');
    const бутон = document.querySelector('[data-gm-wish-hide]');
    if (!тяло || !бутон) return;

    тяло.classList.toggle('gm-collection__body--folded', folded);
    бутон.setAttribute('aria-expanded', folded ? 'false' : 'true');

    const надпис = бутон.querySelector('[data-gm-hide-label]');
    const дума = тяло.dataset;
    if (надпис) {
      надпис.textContent = folded
        ? document.querySelector('[data-gm-wishlist-page]').dataset.gmWordFiltersShow
        : document.querySelector('[data-gm-wishlist-page]').dataset.gmWordFiltersHide;
    }
  }

  document.addEventListener('click', (event) => {
    /* Подредбата: бутон в падащия списък, не връзка — няма адрес за
       презареждане. Отметката и надписът на бутона се местят тук. */
    const избор = event.target.closest('[data-gm-wish-sort]');
    if (избор) {
      sortBy = избор.dataset.gmWishSort;
      const кутия = избор.closest('[data-gm-select-box]');
      for (const друг of кутия.querySelectorAll('[data-gm-wish-sort]')) {
        const е = друг === избор;
        друг.classList.toggle('gm-select__option--current', е);
        if (е) друг.setAttribute('aria-current', 'true');
        else друг.removeAttribute('aria-current');
        const отметка = друг.querySelector('svg');
        if (отметка) отметка.hidden = !е;
      }
      const стойност = document.querySelector('[data-gm-wish-sort-value]');
      if (стойност) стойност.textContent = избор.textContent.trim();
      кутия.open = false;
      refreshPage();
      return;
    }

    const чип = event.target.closest('[data-gm-wish-drop]');
    if (чип) {
      picked[чип.dataset.gmWishDrop].delete(чип.getAttribute('value'));
      refreshPage();
      return;
    }

    if (event.target.closest('[data-gm-wish-clear]')) {
      for (const набор of Object.values(picked)) набор.clear();
      refreshPage();
      return;
    }

    /* «Скрий филтрите» — същото поведение като в колекциите: сваля лявата
       колона и оставя само резултатите. */
    /* «Скрий филтрите» — дума по дума като в колекциите: същият клас
       `--folded`, същата смяна на надписа («Скрий филтрите» ⇄ «Филтри») и
       същата памет в localStorage, за да се върне човекът както го е оставил.
       Първия път сложих свой клас и надписът не се мени — бутонът лъжеше, а
       колоната оставаше най-отгоре по цялата ширина. */
    const скрий = event.target.closest('[data-gm-wish-hide]');
    if (скрий) {
      const сгънато = скрий.getAttribute('aria-expanded') !== 'false';
      applyFold(сгънато);
      try {
        localStorage.setItem(FOLD_KEY, сгънато ? '1' : '0');
      } catch (error) {
        /* паметта не е задължителна */
      }
    }
  });

  /* Един ред в кошницата. Формата остава работеща и без JavaScript — тук само
     се прихваща, за да не се напуска страницата. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-gm-wish-add]');
    if (!form) return;

    event.preventDefault();
    const button = form.querySelector('button');
    if (button && button.dataset.gmBusy === 'true') return;
    if (button) button.dataset.gmBusy = 'true';

    try {
      const id = form.querySelector('[name="id"]').value;
      await addToCart([{ id: Number(id), quantity: 1 }]);
    } catch (error) {
      form.submit();
      return;
    } finally {
      if (button) button.dataset.gmBusy = 'false';
    }
  });

  /* Всички налични наведнъж — една заявка, не по една на ред. */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-gm-wishlist-add-all]');
    if (!button || button.disabled) return;

    const p = pageParts();
    if (!p) return;

    const items = [...p.rows.querySelectorAll('[data-gm-variant-id]')].map((row) => ({
      id: Number(row.dataset.gmVariantId),
      quantity: 1,
    }));
    if (!items.length) return;

    button.disabled = true;
    button.textContent = button.dataset.gmLabelBusy || button.textContent;
    if (p.bulkError) p.bulkError.hidden = true;

    try {
      await addToCart(items);
    } catch (error) {
      if (p.bulkError) {
        p.bulkError.textContent = p.page.dataset.gmWordFailed || '';
        p.bulkError.hidden = !p.bulkError.textContent;
      }
    } finally {
      button.textContent = button.dataset.gmLabelIdle || button.textContent;
      button.disabled = false;
    }
  });

  /*
    Адресите на плочите се сменят и в браузъра остават старите. Пренасочванията
    важат само месец-два, затова преди да се рисува каквото и да е, местният
    списък се дава на ERP за превод: `resolve` връща същия брой адреси в същия
    ред, като сменените са заменени. Чак след това се чертаят сърцата, иначе
    запазена плоча изглежда празна, повторното натискане я записва втори път, а
    след махането на пренасочванията редът изчезва.

    Мълчи ли ERP, върне ли грешка или друга дължина — списъкът остава какъвто е.
    По-добре стар адрес, който още се пренасочва, отколкото изгубен ред.
  */
  async function resolveHandles(list) {
    if (!list.length) return list;

    try {
      const { ok, data } = await post({ action: 'resolve', handles: list });
      if (!ok || !data || !Array.isArray(data.handles)) return list;

      const next = data.handles;
      if (next.length !== list.length) return list;
      if (!next.every((handle) => typeof handle === 'string' && handle)) return list;

      if (next.some((handle, index) => handle !== list[index])) local.write(next);
      return next;
    } catch (error) {
      return list;
    }
  }

  async function start() {
    const mine = await resolveHandles(local.read());

    try {
      const listed = await post({ action: 'list' });
      loggedIn = !!(listed.data && listed.data.logged_in);

      if (loggedIn && mine.length) {
        /* Номерът влиза и във вливането: ERP прехвърля записите на този
           браузър към клиента и връща колко е поел (`claimed`). */
        const merged = await post(withVisitor({ action: 'merge', handles: mine }));
        absorb(merged.data);
        local.clear();
      } else if (loggedIn) {
        absorb(listed.data);
      } else {
        handles = new Set(mine);
        added = new Map();
      }
    } catch (error) {
      // Магазинът мълчи — анонимният си запазва своя списък, влезлият остава
      // без сърца, вместо да получи чужди.
      handles = new Set(mine);
      added = new Map();
    }

    ready = true;
    paint();
    await renderPage();

    catchUpVisitor();
  }

  /* Номерът може да се роди при човек, който отдавна има запазени плочи —
     например днес е дал съгласие. Тогава те не са броени никога. Пращат се по
     едно, най-много двеста: списък от хиляда е по-вероятно повреда, отколкото
     жив човек, а и не искаме да заливаме точката. */
  function catchUpVisitor() {
    if (!visitorIsNew || loggedIn) return;
    visitorIsNew = false;
    for (const handle of [...handles].slice(0, 200)) {
      post(withVisitor({ action: 'add', handle })).catch(() => {});
    }
  }

  /* ТОЧКАТА ЗА СЪГЛАСИЕ не е на страницата по подразбиране: Shopify я дава
     само при поискване. Без нея `customerPrivacy` липсва, проверката връща
     «не», номер не се създава и ERP не брои никого — тихо и незабелязано.
     Затова се иска изрично, а щом дойде, се проверява наново.

     Зарежда се ЕДИН ПЪТ и след останалото: не бива да бави сърцата. Падне ли
     заявката, всичко работи както преди. */
  function askForConsentApi() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) return;
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;
        if (consentGiven()) {
          visitor();
          catchUpVisitor();
        }
      });
    } catch (error) {
      /* без точка за съгласие просто не се брои */
    }
  }

  /* Съгласието може да се даде или оттегли, докато човекът е на сайта.
     Shopify съобщава това със събитие; оттеглянето трие ключа веднага, а не
     чак при следващото зареждане. */
  document.addEventListener('visitorConsentCollected', () => {
    if (!consentGiven()) forgetVisitor();
    else visitor();
  });

  window.gmWishlist = {
    has: (handle) => handles.has(handle),
    all: () => [...handles],
    isLoggedIn: () => loggedIn,
    isReady: () => ready,
    refresh: start,
  };

  function boot() {
    start();
    askForConsentApi();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
